/**
 * ConsentReadPage — the lab-staff consent-read surface (PP-0/D-23). Enter a patient id; the screen
 * relays the LIVE central consent state for `publication_novera` via GET /portal/publish-gate/:patientId
 * (canPublishToNovera → isPublicationConsented, default-closed, never a cached authorization) and
 * renders the verdict as COLOR + GLYPH + TEXT. The publish-gate is the consent's live recheck, so a
 * `can_publish` of true === an active consent.
 *
 * Cloud-only: when the module is absent (route 404s) the screen renders the calm "indisponible"
 * panel instead of an error. Full 5-state DoD; sans-droit is this screen's own ForbiddenState (the
 * module gate is empty so each screen gates on its own read capability).
 */
import { useState } from "react";
import {
  ErrorState,
  ForbiddenState,
  LoadingState,
  OfflineState,
} from "@/ui";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { PP_CAPS, isModuleAbsent, usePublishGate } from "../api";
import { consentVisual } from "../lib";
import { clinicalState } from "@/design/theme";
import { PageHeader } from "../components/PageHeader";
import { ModuleAbsentState } from "../components/ModuleAbsentState";
import { VerdictPanel } from "../components/VerdictPanel";
import { LookupForm } from "../components/LookupForm";

export function ConsentReadPage() {
  const { t } = useT();
  const online = useUiStore((s) => s.online);
  const canRead = useCan(PP_CAPS.consentementRead);

  const [patientId, setPatientId] = useState("");
  const gate = usePublishGate(patientId, canRead && online);

  if (!canRead) return <ForbiddenState requiredCapabilities={[PP_CAPS.consentementRead]} />;

  const header = (
    <PageHeader title={t("pp.consent.title")} subtitle={t("pp.consent.subtitle")} />
  );

  // Cloud-only degradation: a 404 on the publish-gate route = the module is not mounted here.
  if (isModuleAbsent(gate.error)) {
    return (
      <Screen>
        {header}
        <ModuleAbsentState />
      </Screen>
    );
  }

  const canPublish = gate.data?.can_publish ?? false;
  const v = consentVisual(canPublish);
  const glyph = canPublish ? clinicalState("normal").glyph : "◇";

  return (
    <Screen>
      {header}

      <LookupForm
        label={t("pp.consent.field.patientId")}
        hint={t("pp.consent.field.patientId.hint")}
        submitLabel={t("pp.consent.lookup")}
        loading={gate.isFetching}
        onSubmit={setPatientId}
      />

      {!online && patientId !== "" && gate.data === undefined ? (
        <OfflineState onRetry={() => void gate.refetch()} cached={false} />
      ) : gate.isLoading && patientId !== "" ? (
        <LoadingState variant="spinner" />
      ) : gate.isError ? (
        <ErrorState
          message={gate.error?.message ?? ""}
          {...(gate.error?.code ? { code: gate.error.code } : {})}
          onRetry={() => void gate.refetch()}
        />
      ) : gate.data && patientId !== "" ? (
        <VerdictPanel
          title={t("pp.consent.verdict.title")}
          patientId={patientId}
          tone={v.tone}
          glyph={glyph}
          verdictLabel={t(v.labelKey)}
          description={t(canPublish ? "pp.consent.accorde.desc" : "pp.consent.revoque.desc")}
          note={t("pp.consent.live.note")}
        />
      ) : null}
    </Screen>
  );
}

function Screen({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>{children}</div>;
}
